import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { BookOpen, Download, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TEAL = '#004C54';
const TEAL_DARK = '#003a40';
const CYAN = '#4FBCC7';
const CREAM = '#F5F3EE';
// Brand accent uses the same cyan used on the PDF covers themselves so the
// homepage mockup matches the actual rebranded PDF.
const ACCENT = '#4FBCC7';

/**
 * Reusable homepage lead-magnet promo section. Renders the exact visual
 * layout the original PlaybookPromo used, parameterized per audience so
 * Athlete / Parent / Coach sections share one source of truth.
 *
 * Required config shape (see LeadMagnetConfigs below for the 3 live audiences):
 *   id                 stable HTML id + section testid namespace (e.g. "athlete-playbook")
 *   eyebrow            mono-accent label above the headline
 *   title              hero headline (string, may contain inline HTML in `titleNode`)
 *   subtitle           paragraph under the headline
 *   bullets            array of 3 short checklist items
 *   submitLabel        button copy in default state
 *   endpoint           API path (POST). e.g. "/api/pre-game/request"
 *   source             lead source string sent in body
 *   fallbackPdfUrl     relative path to the raw PDF for the success state
 *   fullPageHref       react-router link to the dedicated landing page
 *   fullPageLinkText   copy for the "or read the full page" link
 *   cover.eyebrow      tiny eyebrow on the PDF cover artwork
 *   cover.titleNode    JSX for the cover title (allows <br/>)
 *   cover.tagline      JSX for the small description on the cover
 */
export const LeadMagnetPromo = ({ config }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!firstName.trim() || !email.trim()) {
      setErr('First name and email are required.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}${config.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          source: config.source,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || 'Could not send the playbook right now.');
      }
      const body = await r.json();
      setPdfUrl(body?.pdf_url || config.fallbackPdfUrl);
      setDone(true);
    } catch (e2) {
      setErr(e2.message || 'Could not send the playbook right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id={config.id}
      className="relative py-16 sm:py-20 overflow-hidden"
      style={{ background: CREAM }}
      data-testid={`${config.id}-section`}
    >
      <div className="max-w-[1100px] mx-auto px-5 sm:px-6">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-center">
          {/* Left: pitch + inline form */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10.5px] font-mono-accent font-bold uppercase tracking-[0.2em]"
              style={{ background: 'rgba(0,76,84,0.08)', color: TEAL }}
              data-testid={`${config.id}-eyebrow`}
            >
              <BookOpen className="h-3 w-3" /> {config.eyebrow}
            </div>
            <h2
              className="font-heading text-[1.9rem] sm:text-[2.4rem] lg:text-[2.7rem] font-bold tracking-tight leading-[1.08] mt-4 mb-4"
              style={{ color: TEAL_DARK }}
              data-testid={`${config.id}-title`}
            >
              {config.title}
            </h2>
            <p
              className="text-[15.5px] sm:text-[17px] leading-relaxed mb-6 max-w-[58ch]"
              style={{ color: '#1f2a32' }}
              data-testid={`${config.id}-sub`}
            >
              {config.subtitle}
            </p>

            <ul className="space-y-2 mb-6">
              {config.bullets.map((t) => (
                <li
                  key={t}
                  className="flex items-start gap-2.5 text-[14.5px]"
                  style={{ color: '#1f2a32' }}
                >
                  <CheckCircle2 className="h-[18px] w-[18px] mt-0.5 flex-shrink-0" style={{ color: TEAL }} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            {!done ? (
              <form onSubmit={submit} className="space-y-2.5 max-w-[460px]" data-testid={`${config.id}-form`}>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  <Input
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="bg-white h-11"
                    data-testid={`${config.id}-firstname`}
                  />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-white h-11"
                    data-testid={`${config.id}-email`}
                  />
                </div>
                {err && (
                  <p
                    className="text-[13px] font-medium text-red-700"
                    data-testid={`${config.id}-error`}
                  >
                    {err}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={loading || !firstName || !email}
                  className="w-full sm:w-auto font-bold h-12 px-7 text-[14.5px] shadow-[0_8px_24px_-8px_rgba(0,76,84,0.45)] active:scale-[0.98]"
                  style={{ background: TEAL, color: '#fff' }}
                  data-testid={`${config.id}-submit`}
                >
                  {loading ? 'Sending…' : config.submitLabel}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <p className="text-[11.5px] mt-1" style={{ color: '#516570' }}>
                  No spam. Unsubscribe anytime. Or{' '}
                  <Link
                    to={config.fullPageHref}
                    className="underline underline-offset-2 font-semibold"
                    style={{ color: TEAL }}
                    data-testid={`${config.id}-fullpage-link`}
                  >
                    {config.fullPageLinkText} →
                  </Link>
                </p>
              </form>
            ) : (
              <div
                className="rounded-xl border p-5 max-w-[480px]"
                style={{ background: 'white', borderColor: 'rgba(0,76,84,0.18)' }}
                data-testid={`${config.id}-success`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5" style={{ color: TEAL }} />
                  <h3 className="font-heading text-[16px] font-bold" style={{ color: TEAL_DARK }}>
                    Check your inbox, {firstName.trim() || 'friend'}.
                  </h3>
                </div>
                <p className="text-[13.5px] leading-relaxed mb-3" style={{ color: '#1f2a32' }}>
                  I just sent it to <strong>{email}</strong>. You can also download it right here:
                </p>
                <Button
                  asChild
                  className="font-bold h-11"
                  style={{ background: TEAL, color: '#fff' }}
                  data-testid={`${config.id}-download-now`}
                >
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-1.5" /> Download the PDF
                  </a>
                </Button>
              </div>
            )}
          </div>

          {/* Right: stylized PDF cover — mirrors the design from the dedicated landing pages */}
          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-12px_rgba(0,39,43,0.4)] mx-auto w-full max-w-[340px] aspect-[3/4]"
            style={{ background: CREAM }}
            data-testid={`${config.id}-cover`}
            aria-hidden="true"
          >
            <div className="absolute inset-x-0 top-0 h-3" style={{ background: ACCENT }} />
            <div className="absolute inset-x-0 bottom-0 h-[28%]" style={{ background: TEAL_DARK }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-7 pt-9 pb-[30%]">
              <div
                className="font-mono-accent uppercase tracking-[0.22em] text-[8.5px] font-bold mb-5"
                style={{ color: TEAL_DARK }}
              >
                {config.cover.eyebrow}
              </div>
              <h3
                className="font-heading font-bold leading-[1.05] mb-3 text-[26px]"
                style={{ color: TEAL_DARK }}
              >
                {config.cover.titleNode}
              </h3>
              <p className="text-[11px] leading-relaxed" style={{ color: '#555' }}>
                {config.cover.tagline}
              </p>
              <div className="w-12 h-[2px] mt-5" style={{ background: ACCENT }} />
            </div>
            <div className="absolute inset-x-0 bottom-0 px-6 pb-5 text-center">
              <p className="font-heading font-bold text-[12.5px] text-white mb-0.5">
                By Coach Varsay Lewis, M.Ed.
              </p>
              <p className="text-[10px]" style={{ color: CYAN }}>
                Founder, Apex Athletic Mind
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


// ---------------------------------------------------------------------------
// Audience configs — drive the 3 sections rendered on the homepage in order
// (Athlete → Parent → Coach). Copy mirrors each PDF's dedicated landing page
// so the homepage section feels like a continuous extension of those pages.
// ---------------------------------------------------------------------------

export const ATHLETE_PLAYBOOK_CONFIG = {
  id: 'athlete-playbook',
  eyebrow: 'Free for athletes · PDF download',
  title: 'Confidence isn’t a feeling. It’s a routine.',
  subtitle:
    'The 60-second pre-game routine I run with state champions. Anchor. Belief. Cue. Three steps, one minute, every game — so you walk in feeling like the version of you that already won.',
  bullets: [
    'The ABC Stack: Anchor word, Belief line, Cue visualization',
    'A 60-second routine you can run on the sideline, bench, or in a bathroom stall',
    'The exact protocol used by a HS soccer player who went from "frozen" to "ice cold" in 3 weeks',
  ],
  submitLabel: 'Send Me the Pre-Game Routine',
  endpoint: '/api/pre-game/request',
  source: 'homepage_pre_game_inline',
  fallbackPdfUrl: '/pre-game-routine.pdf',
  fullPageHref: '/pre-game',
  fullPageLinkText: 'read the full pre-game page',
  cover: {
    eyebrow: 'Free Playbook · For Athletes',
    titleNode: (
      <>
        The 60-Second
        <br />
        Pre-Game Routine
      </>
    ),
    tagline: (
      <>
        Anchor. Belief. Cue.
        <br />
        One minute. Every game.
        <br />
        Confidence on demand.
      </>
    ),
  },
};

export const PARENT_PLAYBOOK_CONFIG = {
  id: 'parent-playbook',
  eyebrow: 'Free for parents · PDF download',
  title: 'The car ride home is the most important coaching moment of the week.',
  subtitle:
    'Almost every parent gets it wrong. The Car-Ride-Home Playbook gives you the 12 phrases that work — and the 5 that quietly destroy your athlete’s confidence.',
  bullets: [
    'The 3 rules for what to say after a tough game',
    '12 phrases that work — tested with hundreds of athletes',
    'The 6 magic words that change everything',
  ],
  submitLabel: 'Send Me the Free Playbook',
  endpoint: '/api/playbook/request',
  source: 'homepage_playbook_inline',
  fallbackPdfUrl: '/car-ride-home-playbook.pdf',
  fullPageHref: '/playbook',
  fullPageLinkText: 'read the full playbook page',
  cover: {
    eyebrow: 'Free Playbook · For Parents of Athletes',
    titleNode: (
      <>
        The Car-Ride-Home
        <br />
        Playbook
      </>
    ),
    tagline: (
      <>
        12 things to say after your kid’s game.
        <br />
        5 things to never say.
        <br />
        1 sentence that fixes almost everything.
      </>
    ),
  },
};

export const COACH_PLAYBOOK_CONFIG = {
  id: 'coach-playbook',
  eyebrow: 'Free for coaches · PDF download',
  title: 'Halftimes are won in the first 30 seconds.',
  subtitle:
    'The 5-minute locker room protocol I run with state champions. Read the team. Reset the team. Re-engage the team. Coach-to-coach. No fluff.',
  bullets: [
    'The Green / Yellow / Red read — call the zone in 30 seconds',
    'A 0:00–5:00 halftime protocol you can run starting at your next game',
    'The one thing to NEVER do in the first 30 seconds (almost every coach gets this wrong)',
  ],
  submitLabel: 'Send Me the Locker Room Reset',
  endpoint: '/api/coaches-reset/request',
  source: 'homepage_coaches_reset_inline',
  fallbackPdfUrl: '/coaches-reset.pdf',
  fullPageHref: '/coaches-reset',
  fullPageLinkText: 'read the full coaches page',
  cover: {
    eyebrow: 'Free Playbook · For Coaches',
    titleNode: (
      <>
        The 5-Minute
        <br />
        Locker Room Reset
      </>
    ),
    tagline: (
      <>
        Read. Reset. Re-engage.
        <br />
        The halftime protocol that wins quarters.
        <br />
        Coach-to-coach. No fluff.
      </>
    ),
  },
};

export default LeadMagnetPromo;
